import type { Writable } from "svelte/store";
import * as filepath from "./filepath";
import { FS } from "./fs";
import type { KoofrConfig } from "./fs";
import { FsDir } from "./fsentry";
import { xhrAsync, XhrOpts } from "./http";
import type { HttpProgress } from "./http";
import { throwIf } from "./util";

// https://app.koofr.net/developers
// The Koofr API answers other origins and allows the Authorization header, so
// the browser calls it directly and the password never reaches our server.
// Koofr wants an app password, sent as HTTP basic auth on every call.

export const koofrBaseURL = "https://app.koofr.net";

export const prop_koofr_content_type = "koofr_content_type";
export const prop_koofr_md5 = "koofr_md5";

interface KoofrFile {
  name?: string;
  // "dir" or "file"
  type?: string;
  // milliseconds since epoch
  modified?: number;
  size?: number;
  contentType?: string;
  hash?: string;
}

interface KoofrMount {
  id?: string;
  name?: string;
  isPrimary?: boolean;
}

class FileSystemKoofr extends FS {
  supportsDirRename = true;

  constructor(config: KoofrConfig) {
    super(config);
  }

  get koofrConfig(): KoofrConfig {
    return this.config as KoofrConfig;
  }

  // url of a call on the account's mount. content is for file bodies.
  url(call: string, params: Record<string, string>, content = false): string {
    let prefix = content ? "/content/api/v2/mounts/" : "/api/v2/mounts/";
    let mount = encodeURIComponent(this.koofrConfig.mountId);
    return `${koofrBaseURL}${prefix}${mount}/files/${call}?${new URLSearchParams(params).toString()}`;
  }

  get auth(): string {
    return koofrAuth(this.koofrConfig.email, this.koofrConfig.password);
  }

  async readDir(dirPath: string, force: boolean, progress: Writable<any>): Promise<FsDir> {
    throwIf(dirPath == "", "empty dir");
    return this.loadDir(dirPath, force, progress, async () => {
      let js = await koofrCall(this.auth, this.url("list", { path: filepath.stripDirSlash(dirPath) }));
      return FsDir.fromWire(dirPath, koofrFilesToWire((js.files || []) as KoofrFile[]));
    });
  }

  async getPublicURL(path: string): Promise<string> {
    // Preview and the download link cannot send the Authorization header.
    // Fetch the file and hand the bytes back as a blob URL.
    let blob = await this.downloadFile(path, () => {});
    return URL.createObjectURL(blob);
  }

  async downloadFile(path: string, httpProgress: HttpProgress): Promise<Blob> {
    let opts = new XhrOpts();
    opts.responseType = "blob";
    opts.headers = { Authorization: this.auth };
    opts.httpProgress = httpProgress;
    let xhr = await koofrXhr(this.url("get", { path: path }, true), opts, "download");
    return xhr.response as Blob;
  }

  async uploadFile(path: string, file: Blob, httpProgress: HttpProgress): Promise<void> {
    let params: Record<string, string> = {
      path: filepath.dir(path) || "/",
      filename: filepath.base(path),
      info: "true",
      // replace a file of the same name instead of saving under a new name
      overwrite: "true",
      autorename: "false",
      overwriteIgnoreNonexisting: "",
    };
    if (file instanceof File) {
      params.modified = String(file.lastModified);
    }
    let form = new FormData();
    form.append("file", file, "dummy");
    let opts = new XhrOpts();
    opts.method = "POST";
    opts.params = form;
    opts.headers = { Authorization: this.auth };
    opts.httpProgress = httpProgress;
    await koofrXhr(this.url("put", params, true), opts, "upload");
  }

  async rename(path: string, newPath: string): Promise<void> {
    throwIf(path == newPath, "path must be != newPath");
    path = filepath.stripDirSlash(path);
    newPath = filepath.stripDirSlash(newPath);
    let body = { toMountId: this.koofrConfig.mountId, toPath: newPath };
    await koofrCall(this.auth, this.url("move", { path: path }), "PUT", body);
  }

  async createDirectory(dirPath: string): Promise<void> {
    dirPath = filepath.stripDirSlash(dirPath);
    throwIf(dirPath == "/", "missing directory name");
    let body = { name: filepath.base(dirPath) };
    await koofrCall(this.auth, this.url("folder", { path: filepath.dir(dirPath) || "/" }), "POST", body);
  }

  // Koofr moves the entry to its trash. A folder goes with everything in it.
  async deleteFile(path: string): Promise<void> {
    path = filepath.stripDirSlash(path);
    throwIf(path == "/", "cannot delete the root folder");
    await koofrCall(this.auth, this.url("remove", { path: path }), "DELETE");
  }
}

export function koofrFilesToWire(files: KoofrFile[]): any[][] {
  let wires: any[][] = [];
  for (let f of files) {
    if (!f.name) {
      continue;
    }
    let isDir = f.type == "dir";
    let size = isDir ? -1 : f.size || 0;
    let lastMod = isDir ? 0 : f.modified || 0;
    let meta: any[] = ["", f.name, size, lastMod, isDir ? [] : null];
    if (!isDir && f.contentType) {
      meta.push(prop_koofr_content_type, f.contentType);
    }
    if (!isDir && f.hash) {
      meta.push(prop_koofr_md5, f.hash);
    }
    wires.push(meta);
  }
  return wires;
}

// The value of the Authorization header.
export function koofrAuth(email: string, password: string): string {
  let bytes = new TextEncoder().encode(email + ":" + password);
  let bin = "";
  for (let b of bytes) {
    bin += String.fromCharCode(b);
  }
  return "Basic " + btoa(bin);
}

/**
 * Check the email and app password. Returns the id of the account's own
 * storage: an account can also see mounts that other people shared with it.
 */
export async function koofrLogin(email: string, password: string): Promise<{ mountId: string }> {
  let js = await koofrCall(koofrAuth(email, password), koofrBaseURL + "/api/v2/mounts");
  let mounts = (js.mounts || []) as KoofrMount[];
  let primary = mounts.find((m) => m.isPrimary && m.id);
  throwIf(!primary, "Koofr did not return the account's storage");
  return { mountId: primary!.id! };
}

function koofrStatusMsg(status: number, what: string): string {
  switch (status) {
    case 401:
      return "Koofr: wrong email or app password";
    case 404:
      return "Koofr: not found";
    case 409:
      return "Koofr: an entry with this name already exists";
  }
  return `Koofr ${what} failed: ${status}`;
}

async function koofrCall(auth: string, url: string, method = "GET", body?: object): Promise<any> {
  let headers: Record<string, string> = { Authorization: auth };
  let init: RequestInit = { method, headers };
  if (body) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(body);
  }
  let rsp: Response;
  try {
    rsp = await fetch(url, init);
  } catch {
    throw new Error("could not connect to Koofr");
  }
  let text = await rsp.text();
  let js: any = {};
  try {
    js = text ? JSON.parse(text) : {};
  } catch {
    js = {};
  }
  if (!rsp.ok) {
    let msg = js?.error?.message;
    if (typeof msg == "string" && msg != "" && rsp.status != 401) {
      throw new Error(`Koofr: ${msg}`);
    }
    throw new Error(koofrStatusMsg(rsp.status, "request"));
  }
  return js;
}

async function koofrXhr(url: string, opts: XhrOpts, what: string): Promise<XMLHttpRequest> {
  try {
    return await xhrAsync(url, opts);
  } catch (e: any) {
    if (e && e.status) {
      throw new Error(koofrStatusMsg(e.status, what));
    }
    throw new Error(`Koofr ${what} failed`);
  }
}

export function makeFileSystemKoofr(c: KoofrConfig): FileSystemKoofr {
  return new FileSystemKoofr(c);
}
